import { FetchBaseQueryError } from '@reduxjs/toolkit/query'

import { readStoredTranslations } from '../features/conventional-genres/helpers/conventionalGenreHelpers'
import {
  ConventionalItemMutation,
  ConventionalItemType,
  InternalConventionalItem,
  SavedConventionalItem,
} from '../features/conventional-genres/types/InternalConventionalItem'
import { internalApiserverApi } from './internal-apiserver'
import { LanguageMaps } from './language'

const conventionalGenresApi = internalApiserverApi.enhanceEndpoints({ addTagTypes: ['internalConventionalItems', 'languageMaps'] }).injectEndpoints({
  endpoints: (builder) => ({
    // Raw items, names not localised.
    getInternalConventionalItems: builder.query<InternalConventionalItem[], ConventionalItemType>({
      query: (type) => `conventional/${type}/list`,
      providesTags: (_result, _error, type) => [{ type: 'internalConventionalItems', id: type }],
    }),
    // The save answers 200 with an empty body, so the item and its translations are read back.
    saveConventionalItem: builder.mutation<SavedConventionalItem, { type: ConventionalItemType; item: ConventionalItemMutation }>({
      async queryFn({ type, item }, _api, _extraOptions, baseQuery) {
        const saved = await baseQuery({ url: `i/conventional/${type}`, method: 'POST', body: item, responseHandler: 'text' })
        if (saved.error) return { error: saved.error }
        const [items, languageMaps] = await Promise.all([baseQuery(`conventional/${type}/list`), baseQuery('language')])
        if (items.error || languageMaps.error) return { error: (items.error ?? languageMaps.error) as FetchBaseQueryError }
        return {
          data: {
            item: (items.data as InternalConventionalItem[]).find((stored) => stored.id === item.id),
            translations: readStoredTranslations(languageMaps.data as LanguageMaps, type, item.id),
          },
        }
      },
      invalidatesTags: (_result, _error, { type }) => [{ type: 'internalConventionalItems', id: type }, 'languageMaps'],
    }),
  }),
})

export const { useGetInternalConventionalItemsQuery, useSaveConventionalItemMutation } = conventionalGenresApi
